import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #0062cc;
  cursor: pointer;
  margin-bottom: 20px;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const PropertyHeader = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  margin-bottom: 10px;
`;

const Address = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 30px;
`;

const Card = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const PropertyImage = styled.div`
  height: 400px;
  background: ${props => props.photo ? `url(${props.photo}) center/cover` : 'linear-gradient(45deg, #f0f0f0, #e0e0e0)'};
  border-radius: 8px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
`;

const MapContainer = styled.div`
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
`;

const SidebarCard = styled(Card)`
  position: sticky;
  top: 20px;
  margin-bottom: 0;
`;

const TenantNotice = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
`;

const NoticeStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  padding: 10px;
  background: ${props => props.sent ? 'rgba(40, 167, 69, 0.1)' : 'rgba(255, 193, 7, 0.1)'};
  border-radius: 6px;
  color: ${props => props.sent ? '#28a745' : '#ffc107'};
  font-weight: 500;
`;

const NoticeDetails = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #dee2e6;
`;

const DeliveryMethod = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 14px;
  color: #666;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 15px;
  background: ${props => props.secondary ? 'white' : '#0062cc'};
  color: ${props => props.secondary ? '#0062cc' : 'white'};
  border: ${props => props.secondary ? '1px solid #0062cc' : 'none'};
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 10px;

  &:hover {
    background: ${props => props.secondary ? '#f8f9fa' : '#0056b3'};
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ComplianceInfo = styled.div`
  margin-top: 15px;
  padding: 15px;
  background: #f0f7ff;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  color: #0062cc;
`;

const PropertyDetails = () => {
  const { address } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [noticeStatus, setNoticeStatus] = useState({
    sent: false,
    timestamp: null,
    deliveryMethods: {
      email: { status: 'pending', timestamp: null },
      sms: { status: 'pending', timestamp: null },
      mail: { status: 'pending', timestamp: null }
    }
  });

  useEffect(() => {
    fetchPropertyByAddress();
  }, [address]);

  const fetchPropertyByAddress = async () => {
    try {
      setLoading(true);
      const decodedAddress = decodeURIComponent(address);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/showingbooker/properties/public`);
      
      if (!response.ok) throw new Error('Failed to fetch property');
      
      const data = await response.json();
      const foundProperty = data.properties.find(prop => {
        const propAddress = `${prop.address.line1}${prop.address.unit ? ` ${prop.address.unit}` : ''}, ${prop.address.city}, ${prop.address.state}`.toLowerCase();
        return propAddress === decodedAddress.toLowerCase();
      });
      
      if (!foundProperty) throw new Error('Property not found');
      
      setProperty(foundProperty);
      setError(null);
    } catch (err) {
      console.error('Error fetching property:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendTenantNotice = async () => {
    try {
      // API call to send tenant notice
      setNoticeStatus({
        sent: true,
        timestamp: new Date().toISOString(),
        deliveryMethods: {
          email: { status: 'delivered', timestamp: new Date().toISOString() },
          sms: { status: 'delivered', timestamp: new Date().toISOString() },
          mail: { status: 'pending', timestamp: null }
        }
      });
    } catch (error) {
      console.error('Error sending tenant notice:', error);
    }
  };

  const resendNotice = async (method) => {
    try {
      // API call to resend notice via specific method
      setNoticeStatus(prev => ({
        ...prev,
        deliveryMethods: {
          ...prev.deliveryMethods,
          [method]: { status: 'delivered', timestamp: new Date().toISOString() }
        }
      }));
    } catch (error) {
      console.error('Error resending notice:', error);
    }
  };

  if (loading) {
    return (
      <Container>
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <h2>Loading property details...</h2>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <h2 style={{ color: '#dc3545' }}>Property Not Found</h2>
          <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
          <ActionButton onClick={() => navigate('/properties')}>
            Back to Properties
          </ActionButton>
        </div>
      </Container>
    );
  }

  if (!property) return null;

  return (
    <Container>
      <BackButton onClick={() => navigate('/properties')}>
        ← Back to Properties
      </BackButton>

      <PropertyHeader>
        <Title>
          {property.address.line1}
          {property.address.unit && ` ${property.address.unit}`}
        </Title>
        <Address>
          {property.address.city}, {property.address.state} {property.address.zip}
        </Address>
      </PropertyHeader>

      <MainGrid>
        {/* Left Column */}
        <div>
          <PropertyImage photo={property.photo_url}>
            {!property.photo_url && 'Property Photo'}
          </PropertyImage>

          <Card>
            <h2>Property Details</h2>
            <DetailsGrid>
              {property.details.property_type && (
                <div>
                  <h3>{property.details.property_type.charAt(0).toUpperCase() + property.details.property_type.slice(1)}</h3>
                  <p>Property Type</p>
                </div>
              )}
              
              {property.details.bedrooms && (
                <div>
                  <h3>{property.details.bedrooms}</h3>
                  <p>Bedrooms</p>
                </div>
              )}
              
              {property.details.bathrooms && (
                <div>
                  <h3>{property.details.bathrooms}</h3>
                  <p>Bathrooms</p>
                </div>
              )}
              
              {property.details.square_feet && (
                <div>
                  <h3>{property.details.square_feet.toLocaleString()}</h3>
                  <p>Square Feet</p>
                </div>
              )}
            </DetailsGrid>
          </Card>

          <Card>
            <h2>Current Tenant</h2>
            <div style={{ marginTop: '20px' }}>
              <p><strong>Name:</strong> {property.tenant?.name || 'Not available'}</p>
              <p><strong>Contact:</strong> {property.tenant?.email || 'Not available'}</p>
              <p><strong>Lease Term:</strong> {property.tenant?.lease_end ? `Through ${new Date(property.tenant.lease_end).toLocaleDateString()}` : 'Not available'}</p>
              <p><strong>Preferred Notice Method:</strong> {property.tenant?.notice_preference || 'All methods'}</p>
            </div>
          </Card>

          <Card>
            <h2>Location</h2>
            <MapContainer>
              <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={{ 
                    lat: property.latitude || 34.0522,
                    lng: property.longitude || -118.2437
                  }}
                  zoom={15}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false
                  }}
                >
                  <Marker 
                    position={{
                      lat: property.latitude || 34.0522,
                      lng: property.longitude || -118.2437
                    }}
                    title={property.address.line1}
                  />
                </GoogleMap>
              </LoadScript>
            </MapContainer>
          </Card>
        </div>

        {/* Right Column */}
        <SidebarCard>
          <h2>Tenant Notice Status</h2>
          
          <TenantNotice>
            <NoticeStatus sent={noticeStatus.sent}>
              {noticeStatus.sent ? '✓ Notice Sent' : '! Notice Required'}
            </NoticeStatus>

            {!noticeStatus.sent ? (
              <>
                <p>24-hour notice required before showing</p>
                <ActionButton onClick={sendTenantNotice}>
                  Send Notice Now
                </ActionButton>
              </>
            ) : (
              <>
                <p>Notice sent on {new Date(noticeStatus.timestamp).toLocaleString()}</p>
                <NoticeDetails>
                  <h4>Delivery Status:</h4>
                  <DeliveryMethod>
                    📧 Email: {noticeStatus.deliveryMethods.email.status}
                    {noticeStatus.deliveryMethods.email.status === 'pending' && (
                      <button onClick={() => resendNotice('email')}>Resend</button>
                    )}
                  </DeliveryMethod>
                  <DeliveryMethod>
                    📱 SMS: {noticeStatus.deliveryMethods.sms.status}
                    {noticeStatus.deliveryMethods.sms.status === 'pending' && (
                      <button onClick={() => resendNotice('sms')}>Resend</button>
                    )}
                  </DeliveryMethod>
                  <DeliveryMethod>
                    📬 Mail: {noticeStatus.deliveryMethods.mail.status}
                    {noticeStatus.deliveryMethods.mail.status === 'pending' && (
                      <button onClick={() => resendNotice('mail')}>Resend</button>
                    )}
                  </DeliveryMethod>
                </NoticeDetails>
              </>
            )}
          </TenantNotice>

          <ActionButton 
            onClick={() => navigate(`/properties/${property.id}/compliance`)}
          >
            View Compliance Log
          </ActionButton>
          
          <ActionButton 
            secondary
            onClick={() => navigate(`/properties/${property.id}/reports`)}
          >
            Generate Report
          </ActionButton>

          <ComplianceInfo>
            <p><strong>LAHD Requirements</strong></p>
            <p>24-hour notice with documented delivery required</p>
          </ComplianceInfo>
        </SidebarCard>
      </MainGrid>
    </Container>
  );
};

export default PropertyDetails;